"use client";

import React, { useState } from "react";
import { usePathname } from "next/navigation";
import { Markup } from "interweave";

import Sites from "src/constants/Sites";

import {
  _isFirstPage,
  _isLastPage,
  getBasePageLink,
  getLastPageNumber,
  getPageNumberAsInt,
  shorten,
} from "helpers/Helper";

import { useAppContext } from "@components/Provider";
import Pagination from "@components/Pagination/Pagination";
import RenderSearchPost from "@components/ContentPost/ContentPost";
import NextImage from "@components/Image/NextImage";

import styles from "./Tags.module.css";

// maps background color to siteid for tag pages
const TagsPageBackGroundColor = (siteId) => {
  const background_siteid_map = new Map([
    [Sites.EARLYBIRDBOOKS, "#B9DFFF"],
    [Sites.THE_LINE_UP, "#000000"],
    [Sites.THEPORTALIST, "#00001c"],
    [Sites.MURDER_MAYHEM, "#141414"],
    [Sites.EXPLORETHEARCHIVE, "#f2e3ce"],
    [Sites.ALOVESOTRUE, "#fec9ee"],
  ]);
  return background_siteid_map.get(siteId);
};

const TagsPageTextColor = (siteId) => {
  const textcolor_siteid_map = new Map([
    [Sites.EARLYBIRDBOOKS, "#000"],
    [Sites.THE_LINE_UP, "#fff"],
    [Sites.THEPORTALIST, "#fff"],
    [Sites.MURDER_MAYHEM, "#fff"],
    [Sites.EXPLORETHEARCHIVE, "#000"],
    [Sites.ALOVESOTRUE, "#000"],
  ]);
  return textcolor_siteid_map.get(siteId);
};

const TagsPageReadMoreButtonColor = (siteId) => {
  const textcolor_siteid_map = new Map([
    [Sites.EARLYBIRDBOOKS, "#2C68B0"],
    [Sites.THE_LINE_UP, "#8A0509"],
    [Sites.THEPORTALIST, "#3C7D9A"],
    [Sites.MURDER_MAYHEM, "#F2DE22"],
    [Sites.EXPLORETHEARCHIVE, "#C7B299"],
    [Sites.ALOVESOTRUE, "#E35866"],
  ]);
  return textcolor_siteid_map.get(siteId);
};

const TagsPageReadMoreButtonTextColor = (siteId) => {
  const textcolor_siteid_map = new Map([
    [Sites.EARLYBIRDBOOKS, "#fff"],
    [Sites.MURDER_MAYHEM, "#000"],
    [Sites.EXPLORETHEARCHIVE, "#000"],
    [Sites.ALOVESOTRUE, "#fff"],
  ]);
  return textcolor_siteid_map.get(siteId);
};

const TagsPageBannerImageSizes = (siteId, isMobile) => {
  if (isMobile) {
    const imageSize_mobile_siteid_map = new Map([
      [Sites.EARLYBIRDBOOKS, { width: 221, height: 171 }],
      [Sites.THE_LINE_UP, { width: 220, height: 165 }],
      [Sites.THEPORTALIST, { width: 348, height: 270 }],
      [Sites.MURDER_MAYHEM, { width: 293, height: 227 }],
      [Sites.EXPLORETHEARCHIVE, { width: 283, height: 219 }],
      [Sites.ALOVESOTRUE, { width: 220, height: 165 }],
    ]);

    return imageSize_mobile_siteid_map.get(siteId) || { width: 0, height: 0 };
  }

  const imageSize_siteid_map = new Map([
    [Sites.EARLYBIRDBOOKS, { width: 430, height: 334 }],
    [Sites.THE_LINE_UP, { width: 430, height: 294 }],
    [Sites.THEPORTALIST, { width: 756, height: 287 }],
    [Sites.MURDER_MAYHEM, { width: 430, height: 444 }],
    [Sites.EXPLORETHEARCHIVE, { width: 430, height: 428 }],
    [Sites.ALOVESOTRUE, { width: 430, height: 294 }],
  ]);

  return imageSize_siteid_map.get(siteId) || { width: 0, height: 0 };
};

const TagsPage = (props) => {
  const pathname = usePathname();
  const { data, isMobile, isTablet, siteId } = useAppContext() || {};
  const [readMore, setReadMore] = useState(false);

  const { SiteSettings } = data || {};
  const defaultTag = props?.data?.[0]?.TagsPage?.defaultTag?.[0] || {};

  let tagData = props?.data?.[0]?.TagsPage?.tags?.[0] || {};

  if (!tagData?.socialtitle) {
    tagData = { ...defaultTag, name: tagData?.name || "" };
  }

  const renderTagDataContent = () => {
    if (isMobile || isTablet) {
      const contentLength = readMore ? tagData?.content?.length : 400;
      return (
        <>
          <Markup content={shorten(tagData?.content || "", contentLength)} />
          {tagData?.content?.length >= 400 && (
            <a className={styles["read_more_button"]} onClick={() => setReadMore(!readMore)}>
              {!readMore ? "READ MORE" : "READ LESS"}
            </a>
          )}
        </>
      );
    } else {
      return <div dangerouslySetInnerHTML={{ __html: tagData?.content || "" }} />;
    }
  };

  const renderResults = () => {
    const tagsPageDetails = Array.isArray(props?.data) ? props.data[0] : {};
    const resultData = tagsPageDetails?.TagsPage || {};
    const categoryLayout = SiteSettings?.Category_Layout;
    const { total } = resultData || { total: 0 };
    const route = pathname || "";
    const slugName = props?.slug ? decodeURIComponent(props.slug) : "";
    const pageSize = props?.pageSize || 0;
    const currentPageNumber = getPageNumberAsInt(props?.pageNumber) || 0;
    const isFirstPage = _isFirstPage(currentPageNumber);
    const isLastPage = _isLastPage(currentPageNumber, total, pageSize);

    if (
      !resultData?.posts ||
      isNaN(currentPageNumber) ||
      currentPageNumber > getLastPageNumber(total)
    ) {
      return <div></div>;
    }

    return (
      <div
        className={[
          resultData.displayName,
          resultData.category && "TagsPage",
          categoryLayout && `alt-${categoryLayout}`,
        ].join(" ")}
      >
        <div className={styles["postCardContainer"]}>
          <RenderSearchPost props={{ ...resultData, isCategoryPage: true }} />
        </div>
        <Pagination
          isFirstPage={isFirstPage}
          isLastPage={isLastPage}
          basePageLink={getBasePageLink(props?.pageName || "", slugName)}
          currentPageNumber={currentPageNumber}
          lastPageNumber={getLastPageNumber(total, pageSize)}
          getLinkHref={route}
          paginationText=""
          pageSize={pageSize}
        />
      </div>
    );
  };

  return (
    <div
      className={styles["bannerTopPadding"]}
      style={{
        "--TagsPageReadMoreButtonColor": TagsPageReadMoreButtonColor(siteId),
        "--TagsPageReadMoreButtonTextColor": TagsPageReadMoreButtonTextColor(siteId),
        "--TagsPageTextColor": TagsPageTextColor(siteId),
        "--TagsPageBackGroundColor": TagsPageBackGroundColor(siteId),
      }}
    >
      {(!tagData?.content || tagData?.content !== "") && (
        <div className={styles["bannerContainer"]}>
          <div className={styles["bannerContainerContent"]}>{renderTagDataContent()}</div>
          <NextImage
            className={styles["bannerContainerImage"]}
            name={tagData?.featured_media?.name || ""}
            Title={tagData?.featured_media?.name || ""}
            Alt_Text={tagData?.featured_media?.name || ""}
            width={TagsPageBannerImageSizes(siteId, isMobile).width}
            height={TagsPageBannerImageSizes(siteId, isMobile).height}
          />
        </div>
      )}
      <div className="tagsSection">
        <div className="cardSection">{renderResults()}</div>
      </div>
    </div>
  );
};

export default TagsPage;
